// src/pages/gerenciar/MeusAtendimentos.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useIsMobile } from "../../hooks/useIsMobile";

import { db } from "../../config/firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import { formatDataHora } from "../../utils/dateFormat";
import { STATUS_ATENDIMENTO, isStatusBloqueado } from "../../config/status";
import { marcarPropostasFinalizadasPorSessao } from "../../services/propostasStatusService";

function renderCodigoOs(atendimento) {
  if (atendimento.osCodigo) return atendimento.osCodigo;
  if (atendimento.osCodigoSequencial != null) {
    return `OS${String(atendimento.osCodigoSequencial).padStart(4, "0")}`;
  }
  if (atendimento.osId) return String(atendimento.osId).slice(0, 8);
  return "-";
}

const MOTIVOS_NAO_REALIZADO = [
  { id: "CLIENTE_NAO_DISPONIVEL", label: "Cliente não disponível" },
  { id: "SEM_ACESSO", label: "Sem acesso ao local" },
  { id: "SEM_PARADA", label: "Equipamento em operação / sem parada" },
  { id: "MATERIAL", label: "Aguardando peça/material" },
  { id: "AUTORIZACAO", label: "Aguardando autorização" },
  { id: "FALHA_NAO_REPRODUZIDA", label: "Falha não reproduzida" },
  { id: "OUTRO", label: "Outro" },
];

function MeusAtendimentos() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [atendimentos, setAtendimentos] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [sessoesAtendimentos, setSessoesAtendimentos] = useState({});
  const [clientesById, setClientesById] = useState({});
  const [setoresById, setSetoresById] = useState({});
  const [sessaoExpandidaId, setSessaoExpandidaId] = useState(null);
  const [sessaoItensById, setSessaoItensById] = useState({});
  const [sessaoItensLoading, setSessaoItensLoading] = useState({});
  const [sessaoPendenciasById, setSessaoPendenciasById] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [carregandoSessoes, setCarregandoSessoes] = useState(true);
  const [erro, setErro] = useState("");
  const [erroSessoes, setErroSessoes] = useState("");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("todos"); // todos | principal | acompanhante
  const [sidebarAberta, setSidebarAberta] = useState(false);

  // Modal de execução/finalização de sessão
  const [modalSessao, setModalSessao] = useState(null); // {id, ...}
  const [modalModo, setModalModo] = useState(null); // 'EXECUTAR' | 'FINALIZAR'
  const [modalLoading, setModalLoading] = useState(false);
  const [modalErro, setModalErro] = useState("");
  const [modalItens, setModalItens] = useState([]); // itens já enriquecidos

  const [pendentesFinalizacao, setPendentesFinalizacao] = useState([]); // [{tipo, refId, label, itemId}]
  const [motivosFinalizacao, setMotivosFinalizacao] = useState({}); // {motivoId: boolean}
  const [obsFinalizacao, setObsFinalizacao] = useState("");
  const [salvandoFinalizacao, setSalvandoFinalizacao] = useState(false);
  const [msgSucesso, setMsgSucesso] = useState("");

  useEffect(() => {
    if (!msgSucesso) return;
    const t = setTimeout(() => setMsgSucesso(""), 3500);
    return () => clearTimeout(t);
  }, [msgSucesso]);

  // Ao abrir o modal em modo EXECUTAR, calcular pendências para mostrar status dos itens
  useEffect(() => {
    if (!modalSessao?.id || modalModo !== "EXECUTAR" || !modalItens.length) return;

    async function calcularPendenciasParaModal() {
      try {
        const pendentes = await calcularPendenciasSessao(modalSessao, modalItens);
        setPendentesFinalizacao(pendentes);
      } catch (e) {
        console.error("Erro ao calcular pendências para modal:", e);
      }
    }

    calcularPendenciasParaModal();
  }, [modalSessao?.id, modalModo, modalItens]);

  useEffect(() => {
    async function carregarMeusAtendimentos() {
      if (!usuarioLogado?.grupoId || !usuarioLogado?.uid) return;

      setCarregando(true);
      setErro("");

      try {
        const ref = collection(db, "atendimentos");
        
        // Buscar atendimentos onde o usuário é técnico principal OU acompanhante
        const q = query(
          ref,
          where("grupoId", "==", usuarioLogado.grupoId),
          where("statusAtendimento", "==", STATUS_ATENDIMENTO.EM_ANDAMENTO)
        );
        
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
          .filter((at) => {
            // Filtrar: usuário é técnico principal OU está na lista de acompanhantes
            const isPrincipal = at.tecnicoId === usuarioLogado.uid;
            const isAcompanhante = (at.acompanhantes || []).some(
              (a) => a.id === usuarioLogado.uid
            );
            return isPrincipal || isAcompanhante;
          })
          .map((at) => ({
            ...at,
            meuPapel: at.tecnicoId === usuarioLogado.uid ? "principal" : "acompanhante",
          }));

        setAtendimentos(lista);
      } catch (e) {
        console.error("Erro ao carregar meus atendimentos:", e);
        setErro("Erro ao carregar seus atendimentos. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarMeusAtendimentos();
  }, [usuarioLogado]);

  useEffect(() => {
    async function carregarClientes() {
      if (!usuarioLogado?.grupoId) return;
      try {
        const snap = await getDocs(
          query(collection(db, "empresas"), where("grupoId", "==", usuarioLogado.grupoId))
        );
        const map = {};
        snap.docs.forEach((d) => {
          map[String(d.id)] = d.data();
        });
        setClientesById(map);
      } catch (e) {
        console.warn("Erro ao carregar clientes (planejamento):", e);
      }
    }

    carregarClientes();
  }, [usuarioLogado?.grupoId]);

  // Carregar setores (academias) para ter acesso aos endereços
  useEffect(() => {
    async function carregarSetores() {
      if (!usuarioLogado?.grupoId) return;
      try {
        const snap = await getDocs(
          query(collection(db, "setores"), where("grupoId", "==", usuarioLogado.grupoId))
        );
        const map = {};
        snap.docs.forEach((d) => {
          map[String(d.id)] = d.data();
        });
        setSetoresById(map);
      } catch (e) {
        console.warn("Erro ao carregar setores (academias):", e);
      }
    }

    carregarSetores();
  }, [usuarioLogado?.grupoId]);

  useEffect(() => {
    async function carregarSessoesTecnico() {
      if (!usuarioLogado?.grupoId || !usuarioLogado?.uid) return;

      setCarregandoSessoes(true);
      setErroSessoes("");

      try {
        const [snapPrincipal, snapAcompanhante] = await Promise.all([
          getDocs(
            query(
              collection(db, "atendimentos_sessoes"),
              where("grupoId", "==", usuarioLogado.grupoId),
              where("tecnicoId", "==", usuarioLogado.uid)
            )
          ),
          getDocs(
            query(
              collection(db, "atendimentos_sessoes"),
              where("grupoId", "==", usuarioLogado.grupoId),
              where("tecnicosAcompanhantesIds", "array-contains", usuarioLogado.uid)
            )
          ),
        ]);

        const map = new Map();
        [...snapPrincipal.docs, ...snapAcompanhante.docs].forEach((docSnap) => {
          map.set(docSnap.id, {
            id: docSnap.id,
            ...docSnap.data(),
          });
        });
        const lista = Array.from(map.values());

        // Meus atendimentos deve listar apenas o que ainda está em aberto / em execução.
        // Quando finaliza, a sessão deve sair desta tela.
        const filtradas = lista.filter((s) => {
          const st = String(s.status || "").toUpperCase().trim();
          const finalizou = !!s.finalizadoEm;
          return st !== "CANCELADA" && st !== "ATENDIDA" && st !== "REALIZADA" && st !== "FINALIZADA" && !finalizou;
        });
        filtradas.sort((a, b) => {
          const da = a.inicioEm?.toDate ? a.inicioEm.toDate() : new Date(a.inicioEm || 0);
          const dbv = b.inicioEm?.toDate ? b.inicioEm.toDate() : new Date(b.inicioEm || 0);
          return dbv.getTime() - da.getTime();
        });

        setSessoes(filtradas);

        const atendimentoIds = Array.from(
          new Set(filtradas.map((s) => String(s.atendimentoId || "")).filter(Boolean))
        );

        const atendimentosMap = {};
        await Promise.all(
          atendimentoIds.map(async (id) => {
            try {
              const docRef = await getDoc(doc(db, "atendimentos_preventiva", id));
              if (docRef.exists()) {
                atendimentosMap[id] = { id: docRef.id, ...docRef.data() };
              }
            } catch (e) {
              console.warn("Erro ao carregar atendimento da sessao:", e);
            }
          })
        );

        setSessoesAtendimentos(atendimentosMap);
      } catch (e) {
        console.error("Erro ao carregar sessoes do tecnico:", e);
        setErroSessoes("Erro ao carregar sessoes do planejamento.");
      } finally {
        setCarregandoSessoes(false);
      }
    }

    carregarSessoesTecnico();
  }, [usuarioLogado]);

  const sessoesFiltradas = useMemo(() => {
    if (!textoFiltro.trim()) return sessoes;
    const termo = textoFiltro.toLowerCase();
    return sessoes.filter((s) => {
      const atendimento = sessoesAtendimentos[String(s.atendimentoId)] || {};
      const clienteNome = clientesById[String(atendimento.clienteId)]?.nome || "";
      return (
        String(atendimento.competencia || "").toLowerCase().includes(termo)
        || String(atendimento.contratoTipo || "").toLowerCase().includes(termo)
        || clienteNome.toLowerCase().includes(termo)
      );
    });
  }, [sessoes, sessoesAtendimentos, clientesById, textoFiltro]);

  async function carregarItensSessao(sessao) {
    if (!sessao?.id || !usuarioLogado?.grupoId) return;
    if (sessaoItensById[sessao.id]) return;

    setSessaoItensLoading((prev) => ({ ...prev, [sessao.id]: true }));
    try {
      const lista = await obterItensSessao(sessao.id, sessao);

      const itensFiltrados = filtrarItensSessao(lista);
      const pendentes = await calcularPendenciasSessao(sessao, itensFiltrados);
      const pendentesIds = pendentes.map((p) => p.itemId).filter(Boolean);
      setSessaoPendenciasById((prev) => ({ ...prev, [sessao.id]: pendentesIds }));
    } catch (e) {
      console.error("Erro ao carregar itens da sessao:", e);
      setSessaoPendenciasById((prev) => ({ ...prev, [sessao.id]: [] }));
    } finally {
      setSessaoItensLoading((prev) => ({ ...prev, [sessao.id]: false }));
    }
  }

  function toggleSessao(sessao) {
    if (sessaoExpandidaId === sessao.id) {
      setSessaoExpandidaId(null);
      return;
    }
    setSessaoExpandidaId(sessao.id);
    if (sessaoItensById[sessao.id]) {
      const itensFiltrados = filtrarItensSessao(sessaoItensById[sessao.id] || []);
      calcularPendenciasSessao(sessao, itensFiltrados)
        .then((pendentes) => {
          const pendentesIds = pendentes.map((p) => p.itemId).filter(Boolean);
          setSessaoPendenciasById((prev) => ({ ...prev, [sessao.id]: pendentesIds }));
        })
        .catch(() => {
          setSessaoPendenciasById((prev) => ({ ...prev, [sessao.id]: [] }));
        });
      return;
    }
    carregarItensSessao(sessao);
  }

  // Expande (abre) a sessão sem alternar/fechar. Usado pelo botão "Iniciar atendimento".
  function expandirSessao(sessao) {
    if (!sessao?.id) return;
    if (sessaoExpandidaId === sessao.id) return;
    setSessaoExpandidaId(sessao.id);
    if (sessaoItensById[sessao.id]) {
      const itensFiltrados = filtrarItensSessao(sessaoItensById[sessao.id] || []);
      calcularPendenciasSessao(sessao, itensFiltrados)
        .then((pendentes) => {
          const pendentesIds = pendentes.map((p) => p.itemId).filter(Boolean);
          setSessaoPendenciasById((prev) => ({ ...prev, [sessao.id]: pendentesIds }));
        })
        .catch(() => {
          setSessaoPendenciasById((prev) => ({ ...prev, [sessao.id]: [] }));
        });
      return;
    }
    carregarItensSessao(sessao);
  }

  const atendimentosFiltrados = atendimentos.filter((at) => {
    // Filtro por tipo (principal/acompanhante)
    if (filtroTipo === "principal" && at.meuPapel !== "principal") return false;
    if (filtroTipo === "acompanhante" && at.meuPapel !== "acompanhante") return false;

    // Filtro por texto
    if (!textoFiltro.trim()) return true;
    const termo = textoFiltro.toLowerCase();
    return (
      (at.maquinaNome || "").toLowerCase().includes(termo) ||
      (at.empresaNome || "").toLowerCase().includes(termo) ||
      (at.setorNome || "").toLowerCase().includes(termo) ||
      (at.defeitoApresentado || "").toLowerCase().includes(termo) ||
      renderCodigoOs(at).toLowerCase().includes(termo)
    );
  });

  function handleContinuar(at) {
    navigate(`/ordens-servico/atendimento/${at.osId}`);
  }

  function handleAbrirOs(osId) {
    if (!osId) return;
    navigate(`/ordens-servico/atendimento/${osId}`, {
      state: { voltarPara: "/gerenciar/meus-atendimentos" },
    });
  }

  function handleAbrirChecklistOs(osId) {
    if (!osId) return;
    navigate(`/checklists/executar-os/${osId}`, {
      state: { voltarPara: "/gerenciar/meus-atendimentos" },
    });
  }

  function handleAbrirChecklistInstalacao(equipamentoId) {
    if (!equipamentoId) return;
    navigate(`/checklists/executar/${equipamentoId}`, {
      state: { voltarPara: "/gerenciar/meus-atendimentos" },
    });
  }

  function handleAbrirPreventiva(preventivaId, competencia) {
    if (!preventivaId) return;
    // Quando abrir a preventiva a partir desta tela, o botão "Voltar" deve retornar aqui.
    navigate(`/preventivas/executar/${preventivaId}`, {
      state: { voltarPara: "/gerenciar/meus-atendimentos", competencia: competencia || null },
    });
  }

  function handleAbrirNoMaps(endereco, titulo = "") {
    if (!endereco || !endereco.trim()) {
      alert("Nenhum endereço disponível para abrir no Google Maps.");
      return;
    }
    const url = `https://www.google.com/maps/search/${encodeURIComponent(endereco)}`;
    window.open(url, "_blank");
  }

  function fecharModalSessao() {
    setModalSessao(null);
    setModalModo(null);
    setModalLoading(false);
    setModalErro("");
    setModalItens([]);
    setPendentesFinalizacao([]);
    setMotivosFinalizacao({});
    setObsFinalizacao("");
    setSalvandoFinalizacao(false);
  }

  function filtrarItensSessao(lista) {
    return (lista || []).filter((it) => {
      const tipo = String(it?.tipo || "").toUpperCase();
      const ehInstalacaoLegada =
        tipo === "EQUIPAMENTO" &&
        !!it?.equipamentoId &&
        !it?.osId &&
        !it?.preventivaId;
      return tipo === "OS" || tipo === "PREVENTIVA" || tipo === "INSTALACAO" || it?.osId || it?.preventivaId || ehInstalacaoLegada;
    });
  }

  async function garantirItensInstalacaoDaSessao(sessao, itensSessao = []) {
    if (!sessao?.id || !usuarioLogado?.grupoId) return 0;

    const itensBase = Array.isArray(itensSessao) ? itensSessao : [];
    if (!itensBase.length) return 0;

    const itensInstalacao = itensBase.filter((item) => {
      const tipo = String(item?.tipo || "").trim().toUpperCase();
      return (
        tipo === "INSTALACAO" ||
        !!item?.propostaInstalacaoId ||
        !!item?.checklistInstalacaoId ||
        (tipo === "EQUIPAMENTO" && !!item?.equipamentoId && !item?.osId && !item?.preventivaId)
      );
    });

    if (!itensInstalacao.length) return 0;

    const atendimento = sessoesAtendimentos[String(sessao?.atendimentoId || "")] || {};
    const grupo = String(usuarioLogado?.grupoId || "").trim();
    const empresa = String(
      atendimento?.empresaId || sessao?.empresaId || atendimento?.clienteId || ""
    ).trim();
    const clienteRef = String(atendimento?.clienteId || empresa || "").trim();

    if (!grupo || !empresa || !clienteRef) return 0;

    const propostaIds = new Set(
      itensInstalacao
        .map((item) => String(item?.propostaInstalacaoId || "").trim())
        .filter(Boolean)
    );

    itensInstalacao.forEach((item) => {
      const tipo = String(item?.tipo || "").trim().toUpperCase();
      const equipamentoLike = String(item?.equipamentoId || "").trim();
      if (tipo === "INSTALACAO" && equipamentoLike && !item?.checklistInstalacaoId) {
        propostaIds.add(equipamentoLike);
      }
    });

    const nomePropostaById = {};
    itensInstalacao.forEach((item) => {
      const propostaId = String(item?.propostaInstalacaoId || "").trim();
      if (!propostaId) return;
      if (!nomePropostaById[propostaId] && item?.propostaInstalacaoNome) {
        nomePropostaById[propostaId] = item.propostaInstalacaoNome;
      }
    });

    const existentesEquipIds = new Set(
      itensInstalacao
        .map((item) => String(item?.equipamentoId || "").trim())
        .filter(Boolean)
    );

    const setorRef = String(
      atendimento?.setorId || atendimento?.academiaId || sessao?.setorId || ""
    ).trim();
    const competencia = String(atendimento?.competencia || "").trim() || null;
    const competenciaMes = competencia ? competencia.slice(0, 7) : null;

    const snapMaquinas = await getDocs(
      query(
        collection(db, "maquinas"),
        where("grupoId", "==", grupo),
        where("empresaId", "==", clienteRef)
      )
    );

    const maquinasElegiveis = snapMaquinas.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((maq) => maq?.checklistInstalacaoAtiva === true && !!maq?.checklistInstalacaoId)
      .filter((maq) => {
        if (!setorRef) return true;
        return String(maq?.setorId || "").trim() === setorRef;
      })
      .filter((maq) => {
        if (!propostaIds.size) return true;
        const propostaId = String(maq?.propostaInstalacaoId || "").trim();
        return !!propostaId && propostaIds.has(propostaId);
      })
      .filter((maq) => !existentesEquipIds.has(String(maq?.id || "").trim()));

    if (!maquinasElegiveis.length) return 0;

    const batch = writeBatch(db);
    maquinasElegiveis.forEach((maq) => {
      const propostaId = String(maq?.propostaInstalacaoId || "").trim() || null;
      const propostaNome = propostaId ? (nomePropostaById[propostaId] || null) : null;
      const refItem = doc(collection(db, "atendimentos_sessao_itens"));
      batch.set(refItem, {
        grupoId: grupo,
        empresaId: empresa,
        atendimentoId: sessao.atendimentoId || null,
        sessaoId: sessao.id,
        clienteId: clienteRef,
        tipo: "INSTALACAO",
        equipamentoId: maq.id,
        equipamentoNome: maq?.nome || maq?.codigo || maq.id,
        checklistInstalacaoId: maq?.checklistInstalacaoId || null,
        checklistInstalacaoNome: maq?.checklistInstalacaoNome || null,
        propostaInstalacaoId: propostaId,
        propostaInstalacaoNome: propostaNome,
        setorId: maq?.setorId || setorRef || null,
        setorNome: maq?.setorNome || null,
        competencia,
        competenciaMes,
        quinzena: atendimento?.quinzena || null,
        createdAt: serverTimestamp(),
      });
    });

    await batch.commit();
    return maquinasElegiveis.length;
  }

  async function obterItensSessao(sessaoId, sessaoRef = null) {
    if (!sessaoId) return [];
    if (sessaoItensById[sessaoId]) return sessaoItensById[sessaoId];

    const sessaoBase = sessaoRef || sessoes.find((s) => String(s.id) === String(sessaoId)) || null;

    const qItens = query(
      collection(db, "atendimentos_sessao_itens"),
      where("grupoId", "==", usuarioLogado.grupoId),
      where("sessaoId", "==", sessaoId)
    );

    let snap = await getDocs(qItens);
    let lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    if (sessaoBase) {
      const criados = await garantirItensInstalacaoDaSessao(sessaoBase, lista);
      if (criados > 0) {
        snap = await getDocs(qItens);
        lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }
    }

    setSessaoItensById((prev) => ({ ...prev, [sessaoId]: lista }));
    return lista;
  }

  function tsToDate(ts) {
    if (!ts) return null;
    try {
      if (typeof ts.toDate === "function") return ts.toDate();
      if (ts instanceof Date) return ts;
    } catch (e) {
      return null;
    }
    return null;
  }

  async function calcularPendenciasSessao(sessao, itensFiltrados) {
    const pendentes = [];

    const checks = itensFiltrados.map(async (it) => {
      const tipo = String(it?.tipo || "").toUpperCase();
      const isOs = tipo === "OS" || it?.osId;
      const isPrev = tipo === "PREVENTIVA" || it?.preventivaId;
      const isInstalacao =
        tipo === "INSTALACAO" ||
        (!!it?.equipamentoId && tipo === "EQUIPAMENTO" && !it?.osId && !it?.preventivaId) ||
        (tipo === "OS" && String(it?.osTipoServico || "").toLowerCase().includes("instal"));

      if (isInstalacao && it?.equipamentoId) {
        try {
          const maqSnap = await getDoc(doc(db, "maquinas", it.equipamentoId));
          const maq = maqSnap.exists() ? maqSnap.data() : null;
          const concluido = maq?.checklistConcluido === true;
          if (!concluido) {
            pendentes.push({
              itemId: it.id,
              tipo: "INSTALACAO",
              refId: it.equipamentoId,
              label: it?.equipamentoNome || maq?.nome || `Instalação ${String(it.equipamentoId).slice(0, 8)}`,
            });
          }
        } catch (e) {
          pendentes.push({
            itemId: it.id,
            tipo: "INSTALACAO",
            refId: it.equipamentoId,
            label: it?.equipamentoNome || `Instalação ${String(it.equipamentoId).slice(0, 8)}`,
          });
        }
        return;
      }

      if (isOs && it?.osId) {
        try {
          const osSnap = await getDoc(doc(db, "ordensServico", it.osId));
          const os = osSnap.exists() ? osSnap.data() : null;
          
          if (isInstalacao) {
            // Para instalações: verifica se checklistConcluido = true
            const concluido = os?.checklistConcluido === true;
            if (!concluido) {
              pendentes.push({
                itemId: it.id,
                tipo: "INSTALACAO",
                refId: it.osId,
                label: it?.osCodigo ? `#${it.osCodigo} - ${it?.osTitulo || "Instalação"}` : (it?.osTitulo || `Instalação ${String(it.osId).slice(0, 8)}`),
              });
            }
          } else {
            // Para OS regular: verifica se não está bloqueada
            const status = os?.statusNome || os?.status || "";
            if (!isStatusBloqueado(status)) {
              pendentes.push({
                itemId: it.id,
                tipo: "OS",
                refId: it.osId,
                label: it?.osCodigo ? `#${it.osCodigo} - ${it?.osTitulo || "OS"}` : (it?.osTitulo || `OS ${String(it.osId).slice(0, 8)}`),
              });
            }
          }
        } catch (e) {
          // Erro ao carregar: considera como pendente
          pendentes.push({
            itemId: it.id,
            tipo: isInstalacao ? "INSTALACAO" : "OS",
            refId: it.osId,
            label: it?.osCodigo ? `#${it.osCodigo} - ${it?.osTitulo || (isInstalacao ? "Instalação" : "OS")}` : (it?.osTitulo || `${isInstalacao ? "Instalação" : "OS"} ${String(it.osId).slice(0, 8)}`),
          });
        }
        return;
      }

      if (isPrev && it?.preventivaId) {
        try {
          const prevSnap = await getDoc(doc(db, "preventivas", it.preventivaId));
          const prev = prevSnap.exists() ? prevSnap.data() : null;
          const ultima = tsToDate(prev?.ultimaExecucao);
          const ref = tsToDate(it?.createdAt) || tsToDate(sessao?.startedAt) || tsToDate(sessao?.inicioEm);

          const ok = !!ultima && (!ref || ultima >= ref);
          if (!ok) {
            pendentes.push({
              itemId: it.id,
              tipo: "PREVENTIVA",
              refId: it.preventivaId,
              label: it?.preventivaNome || it?.maquinaNome || `Preventiva ${String(it.preventivaId).slice(0, 8)}`,
            });
          }
        } catch (e) {
          pendentes.push({
            itemId: it.id,
            tipo: "PREVENTIVA",
            refId: it.preventivaId,
            label: it?.preventivaNome || it?.maquinaNome || `Preventiva ${String(it.preventivaId).slice(0, 8)}`,
          });
        }
      }
    });

    await Promise.all(checks);
    return pendentes;
  }

  async function tentarGerarRelatorioSessao(sessaoId) {
    try {
      await fetch("/api/atendimentos/relatorio/gerar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ grupoId: usuarioLogado?.grupoId, sessaoId }),
      });
    } catch (e) {
      // best-effort
    }
  }

  async function consumirCotaEmergencialSePendente(sessaoRef) {
    if (!sessaoRef?.id) return;
    try {
      const snapSessao = await getDoc(doc(db, "atendimentos_sessoes", sessaoRef.id));
      if (!snapSessao.exists()) return;
      const dados = snapSessao.data() || {};
      const pendente = dados?.emergencialConsumoPendente === true;
      const consumido = dados?.emergencialConsumido === true;
      const contratoId = String(dados?.contratoId || "").trim();
      if (!pendente || consumido || !contratoId) return;

      await updateDoc(doc(db, "financeiro_contratos", contratoId), {
        visitasEmergenciaisUsadas: increment(1),
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "atendimentos_sessoes", sessaoRef.id), {
        emergencialConsumido: true,
        emergencialConsumoPendente: false,
        emergencialConsumidoEm: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("Erro ao consumir cota emergencial na finalização:", e);
    }
  }

  async function abrirModalExecucaoSessao(sessao) {
    if (!sessao?.id) return;
    setModalSessao(sessao);
    setModalModo("EXECUTAR");
    setModalErro("");
    setModalLoading(true);
    try {
      const itens = await obterItensSessao(sessao.id, sessao);
      setModalItens(filtrarItensSessao(itens));
    } catch (e) {
      setModalErro("Não foi possível carregar os itens desta sessão.");
      setModalItens([]);
    } finally {
      setModalLoading(false);
    }
  }

  async function handleIniciarSessao(sessao) {
    if (!sessao?.id) return;

    // Ao iniciar: gravar horário de início (apenas uma vez) e expandir para mostrar os itens.
    // (Substitui o popup antigo; o botão Iniciar agora faz o papel do antigo "Ver itens").
    try {
      if (!sessao.startedAt) {
        await updateDoc(doc(db, "atendimentos_sessoes", sessao.id), {
          startedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // Atualiza estado local (best-effort) para não depender de refresh.
        setSessoes((prev) =>
          prev.map((s) => (s.id === sessao.id ? { ...s, startedAt: new Date() } : s))
        );
      }
    } catch (e) {
      // best-effort
    }

    // Ao iniciar, a intenção é abrir/mostrar os itens (sem fechar caso já esteja aberto).
    expandirSessao(sessao);
  }

  async function finalizarSessaoSemPopup(sessao) {
    await updateDoc(doc(db, "atendimentos_sessoes", sessao.id), {
      status: "ATENDIDA",
      fimEm: serverTimestamp(),
      finalizadoEm: serverTimestamp(),
      finalizadoPorId: usuarioLogado.uid,
      finalizadoPorNome: usuarioLogado.nome || usuarioLogado.email || "",
      updatedAt: serverTimestamp(),
    });
    await marcarPropostasFinalizadasPorSessao(sessao.id);
    await consumirCotaEmergencialSePendente(sessao);
    // Remove da listagem imediatamente (sem precisar recarregar a página)
    setSessoes((prev) => prev.filter((s) => s.id !== sessao.id));
    if (sessaoExpandidaId === sessao.id) setSessaoExpandidaId(null);
    tentarGerarRelatorioSessao(sessao.id);
  }

  async function handleFinalizarSessao(sessao) {
    if (!sessao?.id) return;
    setMsgSucesso("");
    setModalErro("");
    try {
      const itens = filtrarItensSessao(await obterItensSessao(sessao.id, sessao));
      const pendentes = await calcularPendenciasSessao(sessao, itens);

      if (pendentes.length === 0) {
        await finalizarSessaoSemPopup(sessao);
        setMsgSucesso("Atendimento finalizado.");
        if (modalSessao?.id === sessao.id) fecharModalSessao();
        return;
      }

      // Há pendências -> abre popup de motivos/observações
      setModalSessao(sessao);
      setModalModo("FINALIZAR");
      setModalItens(itens);
      setPendentesFinalizacao(pendentes);
      setMotivosFinalizacao({});
      setObsFinalizacao("");
    } catch (e) {
      setModalErro("Erro ao verificar pendências da sessão.");
    }
  }

  async function confirmarFinalizacaoComPendencias() {
    if (!modalSessao?.id) return;
    if (salvandoFinalizacao) return;

    const motivos = Object.keys(motivosFinalizacao).filter((k) => motivosFinalizacao[k]);
    const obs = (obsFinalizacao || "").trim();

    if (motivos.length === 0 && !obs) {
      setModalErro("Selecione pelo menos 1 motivo ou descreva uma observação.");
      return;
    }

    setSalvandoFinalizacao(true);
    setModalErro("");
    try {
      // 1) Finaliza sessão atual com log das pendências
      await updateDoc(doc(db, "atendimentos_sessoes", modalSessao.id), {
        status: "ATENDIDA",
        fimEm: serverTimestamp(),
        finalizadoEm: serverTimestamp(),
        finalizadoPorId: usuarioLogado.uid,
        finalizadoPorNome: usuarioLogado.nome || usuarioLogado.email || "",
        finalizacaoMotivos: motivos,
        finalizacaoObservacoes: obs || null,
        finalizacaoPendentes: pendentesFinalizacao.map((p) => ({
          tipo: p.tipo,
          refId: p.refId,
          label: p.label,
        })),
        updatedAt: serverTimestamp(),
      });

      await marcarPropostasFinalizadasPorSessao(modalSessao.id);
      await consumirCotaEmergencialSePendente(modalSessao);

      // Remove da listagem imediatamente (sem precisar recarregar a página)
      setSessoes((prev) => prev.filter((s) => s.id !== modalSessao.id));
      if (sessaoExpandidaId === modalSessao.id) setSessaoExpandidaId(null);

      // 2) Cria nova sessão PENDENTE (para replanejamento) e move os itens pendentes
      const novaSessaoRef = await addDoc(collection(db, "atendimentos_sessoes"), {
        grupoId: usuarioLogado.grupoId,
        empresaId: modalSessao.empresaId || null,
        atendimentoId: modalSessao.atendimentoId,
        tecnicoId: null,
        inicioEm: null,
        fimEm: null,
        status: "PENDENTE",
        observacoes: null,
        origemSessaoId: modalSessao.id,
        replanejamentoMotivos: motivos,
        replanejamentoObservacoes: obs || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        criadoPorId: usuarioLogado.uid,
        criadoPorNome: usuarioLogado.nome || usuarioLogado.email || "",
      });

      const batch = writeBatch(db);
      pendentesFinalizacao.forEach((p) => {
        batch.update(doc(db, "atendimentos_sessao_itens", p.itemId), {
          sessaoId: novaSessaoRef.id,
          replanejadoDeSessaoId: modalSessao.id,
          replanejadoEm: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();

      tentarGerarRelatorioSessao(modalSessao.id);
      setMsgSucesso("Atendimento finalizado. Itens pendentes foram enviados para replanejamento.");
      fecharModalSessao();
    } catch (e) {
      setModalErro("Não foi possível finalizar o atendimento. Verifique sua conexão e tente novamente.");
    } finally {
      setSalvandoFinalizacao(false);
    }
  }

  const Layout = isMobile ? LayoutMobile : LayoutDesktop;

  const conteudo = (
    <section className={isMobile ? "m-card" : "pc-card"}>
      {!isMobile && (
        <header className="pc-form-header">
          <h3>Meus Atendimentos em Andamento</h3>
          <p>
            Aqui você encontra os atendimentos que está realizando ou acompanhando.
            Clique em "Continuar" para retomar de onde parou.
          </p>
        </header>
      )}

      {msgSucesso && (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 10,
            background: "rgba(46, 160, 67, 0.15)",
            border: "1px solid rgba(46, 160, 67, 0.35)",
          }}
        >
          {msgSucesso}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          className={isMobile ? "m-input" : "pc-input"}
          value={textoFiltro}
          onChange={(e) => setTextoFiltro(e.target.value)}
          placeholder="Buscar por máquina, empresa, setor, OS..."
          style={{ width: "100%", maxWidth: isMobile ? "100%" : 400 }}
        />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button
          className={isMobile 
            ? (filtroTipo === "todos" ? "m-btn-primary" : "m-btn-secondary")
            : `pc-btn ${filtroTipo === "todos" ? "pc-btn-primario" : "pc-btn-secundario"}`
          }
          onClick={() => setFiltroTipo("todos")}
          style={{ padding: "8px 12px", fontSize: "0.8rem" }}
        >
          Todos ({atendimentos.length})
        </button>
        <button
          className={isMobile 
            ? (filtroTipo === "principal" ? "m-btn-primary" : "m-btn-secondary")
            : `pc-btn ${filtroTipo === "principal" ? "pc-btn-primario" : "pc-btn-secundario"}`
          }
          onClick={() => setFiltroTipo("principal")}
          style={{ padding: "8px 12px", fontSize: "0.8rem" }}
        >
          Responsável ({atendimentos.filter((a) => a.meuPapel === "principal").length})
        </button>
        <button
          className={isMobile 
            ? (filtroTipo === "acompanhante" ? "m-btn-primary" : "m-btn-secondary")
            : `pc-btn ${filtroTipo === "acompanhante" ? "pc-btn-primario" : "pc-btn-secundario"}`
          }
          onClick={() => setFiltroTipo("acompanhante")}
          style={{ padding: "8px 12px", fontSize: "0.8rem" }}
        >
          Acompanhando ({atendimentos.filter((a) => a.meuPapel === "acompanhante").length})
        </button>
      </div>

      {erro && (
        <div
          style={{
            background: "#7f1d1d",
            color: "#fecaca",
            padding: 8,
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {erro}
        </div>
      )}

      {carregando ? (
        <p style={{ padding: 10 }}>Carregando seus atendimentos...</p>
      ) : atendimentosFiltrados.length === 0 ? null : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {atendimentosFiltrados.map((at) => (
            <div
              key={at.id}
              className={isMobile ? "m-card" : "pc-card"}
              style={{
                padding: isMobile ? 12 : 16,
                borderRadius: 12,
                border: at.meuPapel === "principal" 
                  ? "2px solid rgba(34, 197, 94, 0.5)" 
                  : "2px solid rgba(59, 130, 246, 0.5)",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                gap: isMobile ? 12 : 16,
              }}
            >
              {/* Badge do papel */}
              <div
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  background: at.meuPapel === "principal"
                    ? "rgba(34, 197, 94, 0.2)"
                    : "rgba(59, 130, 246, 0.2)",
                  color: at.meuPapel === "principal" ? "#22c55e" : "#60a5fa",
                  flexShrink: 0,
                  alignSelf: isMobile ? "flex-start" : "center",
                }}
              >
                {at.meuPapel === "principal" ? "Responsável" : "Acompanhando"}
              </div>

              {/* Informações do atendimento */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                  <strong style={{ fontSize: 14 }}>{renderCodigoOs(at)}</strong>
                  {at.subnumero && (
                    <span style={{ fontSize: 12, opacity: 0.7 }}>SUB {at.subnumero}</span>
                  )}
                </div>
                <div style={{ fontSize: 13, opacity: 0.9 }}>
                  <strong>{at.maquinaNome || "-"}</strong>
</div>
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                  {at.empresaNome || "-"} • {at.setorNome || "-"}
                </div>
                <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                  Iniciado em: {formatDataHora(at.dataInicio)}
                </div>
                {at.meuPapel === "acompanhante" && at.tecnicoNome && (
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                    <span style={{ color: "#22c55e" }}>Técnico responsável:</span> {at.tecnicoNome}
                  </div>
                )}
                {at.meuPapel === "principal" && at.acompanhantes?.length > 0 && (
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                    <span style={{ color: "#60a5fa" }}>Acompanhantes:</span>{" "}
                    {at.acompanhantes.map((a) => a.nome).join(", ")}
                  </div>
                )}
              </div>

              {/* Botões de ação */}
              <div style={{ display: "flex", gap: 8, flexShrink: 0, width: isMobile ? "100%" : "auto", flexDirection: isMobile ? "column" : "row" }}>
                {(() => {
                  // Tentar obter endereço do setor
                  const setorId = at.progresso?.setorId || at.setorId || at.academiaId;
                  const setor = setorId ? setoresById[String(setorId)] : null;
                  const endereco = setor?.endereco || "";
                  return endereco ? (
                    <button
                      className={isMobile ? "m-btn-secondary" : "pc-btn pc-btn-secundario"}
                      onClick={() => handleAbrirNoMaps(endereco, at.setorNome)}
                      style={{
                        padding: isMobile ? "10px 16px" : "12px 16px",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        flexShrink: 0,
                        width: isMobile ? "100%" : "auto",
                      }}
                      title={`Abrir ${endereco} no Google Maps`}
                    >
                      🗺️ Localização
                    </button>
                  ) : null;
                })()}
                {/* Botão Iniciar Atendimento - visível apenas para o responsável e se não iniciado */}
                {at.meuPapel === "principal" && !at.dataInicio && (
                  <button
                    className={isMobile ? "m-btn-success" : "pc-btn pc-btn-success"}
                    onClick={() => alert("Iniciar Atendimento: lógica será implementada aqui.")}
                    style={{
                      padding: isMobile ? "10px 16px" : "12px 20px",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      flexShrink: 0,
                      width: isMobile ? "100%" : "auto",
                      background: isMobile ? "#22c55e" : undefined,
                      color: isMobile ? "#fff" : undefined,
                    }}
                  >
                    Iniciar Atendimento
                  </button>
                )}
                {/* Botão Continuar */}
                <button
                  className={isMobile ? "m-btn-primary" : "pc-btn pc-btn-primario"}
                  onClick={() => handleContinuar(at)}
                  style={{
                    padding: isMobile ? "10px 16px" : "12px 24px",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    flexShrink: 0,
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  Continuar
                </button>
                {/* Botão Finalizar Atendimento - visível apenas para o responsável e se já iniciado */}
                {at.meuPapel === "principal" && at.dataInicio && (
                  <button
                    className={isMobile ? "m-btn-warning" : "pc-btn pc-btn-warning"}
                    onClick={() => alert("Finalização: lógica/modal pendências será implementada aqui.")}
                    style={{
                      padding: isMobile ? "10px 16px" : "12px 20px",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      flexShrink: 0,
                      width: isMobile ? "100%" : "auto",
                      background: isMobile ? "#fbbf24" : undefined,
                      color: isMobile ? "#78350f" : undefined,
                    }}
                  >
                    Finalizar Atendimento
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <section className={!isMobile ? "pc-card" : ""} style={{ marginTop: 20 }}>
        <header
          className={!isMobile ? "pc-form-header" : undefined}
          style={isMobile ? { marginBottom: 12 } : undefined}
        >
          <h3>Atendimentos do Planejamento</h3>
          <p>
            Sessoes atribuidas a voce. Use <strong>Iniciar atendimento</strong> para registrar o inicio e abrir os itens.
            Use <strong>Ver itens</strong> para visualizar/fechar os itens sem registrar inicio. Use <strong>Finalizar atendimento</strong> para encerrar.
          </p>
        </header>

        {erroSessoes && (
          <div
            style={{
              background: "#7f1d1d",
              color: "#fecaca",
              padding: 8,
              borderRadius: 8,
              marginBottom: 12,
            }}
          >
            {erroSessoes}
          </div>
        )}

        {carregandoSessoes ? (
          <p style={{ padding: 10 }}>Carregando sessoes...</p>
        ) : sessoesFiltradas.length === 0 ? (
          <div style={{ padding: 16, opacity: 0.7 }}>Nenhuma sessao atribuida.</div>
        ) : isMobile ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {sessoesFiltradas.map((s) => {
              const atendimento = sessoesAtendimentos[String(s.atendimentoId)] || {};
              const clienteNome = clientesById[String(atendimento.clienteId)]?.nome || "-";
              const ini = s.inicioEm ? formatDataHora(s.inicioEm) : "-";
              const fim = s.fimEm ? formatDataHora(s.fimEm) : "-";
              const itens = sessaoItensById[s.id] || [];
              const itensFiltrados = filtrarItensSessao(itens);
              const itensCount = itens.length ? itensFiltrados.length : "-";

              return (
                <div
                  key={s.id}
                  style={{
                    border: "1px solid rgba(148,163,184,0.25)",
                    borderRadius: 12,
                    padding: 12,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "1rem" }}>{clienteNome}</div>
                      <div style={{ opacity: 0.85, fontSize: "0.9rem", marginTop: 2 }}>
                        {atendimento.competencia || "-"}
                      </div>
                      <div style={{ opacity: 0.75, fontSize: "0.85rem", marginTop: 6 }}>
                        Inicio: {ini} • Fim: {fim}
                      </div>
                      <div style={{ opacity: 0.75, fontSize: "0.85rem", marginTop: 2 }}>
                        Status: {s.status || "-"} • Itens: {itensCount}
                      </div>
                    </div>
                    {s.enderecoDestino && (
                      <button
                        type="button"
                        className="m-btn-secondary"
                        onClick={() => handleAbrirNoMaps(s.enderecoDestino, "")}
                        title={`Abrir ${s.enderecoDestino} no Google Maps`}
                      >
                        🗺️
                      </button>
                    )}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                    <button type="button" className="m-btn-success" onClick={() => handleIniciarSessao(s)}>
                      Iniciar atendimento
                    </button>

                    <button type="button" className="m-btn-secondary" onClick={() => toggleSessao(s)}>
                      {sessaoExpandidaId === s.id ? "Fechar itens" : "Ver itens"}
                    </button>

                    <button type="button" className="m-btn-warning" onClick={() => handleFinalizarSessao(s)}>
                      Finalizar atendimento
                    </button>
                  </div>

                  {sessaoExpandidaId === s.id && (
                    <div
                      style={{
                        marginTop: 12,
                        borderTop: "1px solid rgba(148,163,184,0.18)",
                        paddingTop: 12,
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      {(() => {
                        const pendentesIds = new Set((sessaoPendenciasById[s.id] || []).map((id) => String(id)));
                        return sessaoItensLoading[s.id] ? (
                        <div style={{ opacity: 0.7 }}>Carregando itens...</div>
                      ) : itensFiltrados.length === 0 ? (
                        <div style={{ opacity: 0.7 }}>Nenhum item nesta sessao.</div>
                      ) : (
                        itensFiltrados.map((it) => {
                          const tipo = String(it?.tipo || "").toUpperCase();
                          const isOs = tipo === "OS" || it?.osId;
                          const isPrev = tipo === "PREVENTIVA" || it?.preventivaId;
                          const isInstalacao =
                            tipo === "INSTALACAO" ||
                            (!!it?.equipamentoId && tipo === "EQUIPAMENTO" && !it?.osId && !it?.preventivaId) ||
                            (isOs && String(it?.osTipoServico || "").toLowerCase().includes("instal"));
                          const isRespondido = !pendentesIds.has(String(it.id));

                          // Extrair preventivaId com fallback para o formato do id (PREVENTIVA:id:competencia)
                          let preventivaId = it?.preventivaId;
                          if (!preventivaId && tipo === "PREVENTIVA" && it?.id) {
                            const parts = String(it.id).split(":");
                            if (parts[0] === "PREVENTIVA" && parts[1]) {
                              preventivaId = parts[1];
                            }
                          }

                          const label = isOs
                            ? (it?.osCodigo ? `#${it.osCodigo}` : "OS")
                            : (isInstalacao
                              ? (it?.equipamentoNome || "Instalação")
                              : (it?.preventivaCodigo || it?.preventivaNome || "Preventiva"));
                          const desc = isOs
                            ? (it?.osTitulo || it?.osId || "-")
                            : (isInstalacao
                              ? (it?.checklistInstalacaoNome || "Checklist de Instalação")
                              : (it?.preventivaNome || it?.maquinaNome || "-"));
                          const detalhes = [it?.setorNome, it?.maquinaNome].filter(Boolean).join(" • ");
                          const tipoLabel = isInstalacao ? "Instalação" : (isOs ? "OS" : "Preventiva");

                          return (
                            <div
                              key={it.id}
                              style={{
                                border: "1px solid rgba(148,163,184,0.18)",
                                borderRadius: 12,
                                padding: 10,
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 10,
                                alignItems: "center",
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 700 }}>{tipoLabel} • {label}</div>
                                <div style={{ opacity: 0.85, marginTop: 2, fontSize: "0.9rem" }}>
                                  {[desc, detalhes].filter(Boolean).join(" • ") || "-"}
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                {(isOs || isInstalacao) && (
                                  <button
                                    type="button"
                                    className="m-btn-secondary"
                                    onClick={() => isInstalacao ? handleAbrirChecklistInstalacao(it.equipamentoId) : handleAbrirOs(it.osId)}
                                  >
                                    {isRespondido ? "Alterar" : (isInstalacao ? "Checklist" : "Abrir OS")}
                                  </button>
                                )}

                                {isOs && String(it?.osTipoServico || "").toLowerCase().includes("instal") && (
                                  <button type="button" className="m-btn-secondary" onClick={() => handleAbrirChecklistOs(it.osId)}>
                                    {isRespondido ? "Alterar" : "Checklist"}
                                  </button>
                                )}
                                {isPrev && (
                                  <button type="button" className="m-btn-secondary" onClick={() => {
                                    console.log("DEBUG: Item preventiva:", { it, preventivaId: preventivaId, tipo: tipo });
                                    handleAbrirPreventiva(preventivaId, it.competencia || modalAtendimento?.competencia || atendimento?.competencia);
                                  }}>
                                    {isRespondido ? "Alterar" : "Abrir preventiva"}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="pc-table-wrapper">
            <table className="pc-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Competencia</th>
                  <th>Inicio</th>
                  <th>Fim</th>
                  <th>Status</th>
                  <th>Itens</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sessoesFiltradas.map((s) => {
                  const atendimento = sessoesAtendimentos[String(s.atendimentoId)] || {};
                  const clienteNome = clientesById[String(atendimento.clienteId)]?.nome || "-";
                  const itens = sessaoItensById[s.id] || [];
                  const itensFiltrados = filtrarItensSessao(itens);
                  const pendentesIds = new Set((sessaoPendenciasById[s.id] || []).map((id) => String(id)));
                  const itensCount = itens.length ? itensFiltrados.length : "-";
                  const ini = s.inicioEm ? formatDataHora(s.inicioEm) : "-";
                  const fim = s.fimEm ? formatDataHora(s.fimEm) : "-";

                  return (
                    <React.Fragment key={s.id}>
                      <tr className={sessaoExpandidaId === s.id ? "planejamento-visitas-sessao-ativa" : ""}>
                        <td>{clienteNome}</td>
                        <td>{atendimento.competencia || "-"}</td>
                        <td>{ini}</td>
                        <td>{fim}</td>
                        <td>{s.status || "-"}</td>
                        <td>{itensCount}</td>
                        <td style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          <button type="button" className="pc-btn pc-btn-primario" onClick={() => handleIniciarSessao(s)}>
                            Iniciar atendimento
                          </button>

                          <button type="button" className="pc-btn pc-btn-secundario" onClick={() => toggleSessao(s)}>
                            {sessaoExpandidaId === s.id ? "Fechar itens" : "Ver itens"}
                          </button>

                          <button type="button" className="pc-btn pc-btn-secundario" onClick={() => handleFinalizarSessao(s)}>
                            Finalizar atendimento
                          </button>
                          {s.enderecoDestino && (
                            <button
                              type="button"
                              className="pc-btn pc-btn-secundario"
                              onClick={() => handleAbrirNoMaps(s.enderecoDestino, "")}
                              title={`Abrir ${s.enderecoDestino} no Google Maps`}
                            >
                              🗺️
                            </button>
                          )}
                        </td>
                      </tr>
                      {sessaoExpandidaId === s.id && (
                        <tr>
                          <td colSpan={7}>
                            <div style={{ padding: "8px 0" }}>
                              {sessaoItensLoading[s.id] ? (
                                <div style={{ opacity: 0.7 }}>Carregando itens...</div>
                              ) : itensFiltrados.length === 0 ? (
                                <div style={{ opacity: 0.7 }}>Nenhum item nesta sessao.</div>
                              ) : (
                                <div className="pc-table-wrapper">
                                  <table className="pc-table">
                                    <thead>
                                      <tr>
                                        <th>Tipo</th>
                                        <th>Item</th>
                                        <th>Detalhes</th>
                                        <th></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {itensFiltrados.map((it) => {
                                        const tipo = String(it?.tipo || "").toUpperCase();
                                        const isOs = tipo === "OS" || it?.osId;
                                        const isPrev = tipo === "PREVENTIVA" || it?.preventivaId;
                                        const isInstalacao =
                                          tipo === "INSTALACAO" ||
                                          (!!it?.equipamentoId && tipo === "EQUIPAMENTO" && !it?.osId && !it?.preventivaId) ||
                                          (isOs && String(it?.osTipoServico || "").toLowerCase().includes("instal"));
                                        const isRespondido = !pendentesIds.has(String(it.id));
                                        
                                        // Extrair preventivaId com fallback para o formato do id (PREVENTIVA:id:competencia)
                                        let preventivaId = it?.preventivaId;
                                        if (!preventivaId && tipo === "PREVENTIVA" && it?.id) {
                                          const parts = String(it.id).split(":");
                                          if (parts[0] === "PREVENTIVA" && parts[1]) {
                                            preventivaId = parts[1];
                                          }
                                        }
                                        
                                        const label = isOs
                                          ? (it?.osCodigo ? `#${it.osCodigo}` : "OS")
                                          : (isInstalacao
                                            ? (it?.equipamentoNome || "Instalação")
                                            : (it?.preventivaCodigo || it?.preventivaNome || "Preventiva"));
                                        const desc = isOs
                                          ? (it?.osTitulo || it?.osId || "-")
                                          : (isInstalacao
                                            ? (it?.checklistInstalacaoNome || "Checklist de Instalação")
                                            : (it?.preventivaNome || it?.maquinaNome || "-"));
                                        const detalhes = [it?.setorNome, it?.maquinaNome].filter(Boolean).join(" • ");
                                        const tipoLabel = isInstalacao ? "Instalação" : (isOs ? "OS" : "Preventiva");

                                        return (
                                          <tr key={it.id}>
                                            <td>{tipoLabel}</td>
                                            <td>{label}</td>
                                            <td>{[desc, detalhes].filter(Boolean).join(" • ") || "-"}</td>
                                            <td>
                                              {(isOs || isInstalacao) && (
                                                <button
                                                  type="button"
                                                  className="pc-btn pc-btn-secundario"
                                                  onClick={() => isInstalacao ? handleAbrirChecklistInstalacao(it.equipamentoId) : handleAbrirOs(it.osId)}
                                                  style={{ marginRight: "8px" }}
                                                >
                                                  {isRespondido ? "Alterar" : (isInstalacao ? "Executar Checklist" : "Abrir OS")}
                                                </button>
                                              )}
                                              {isPrev && (
                                                <button
                                                  type="button"
                                                  className="pc-btn pc-btn-secundario"
                                                  onClick={() => {
                                                    console.log("DEBUG (desktop): Item preventiva:", { it, preventivaId: preventivaId, tipo: tipo });
                                                    handleAbrirPreventiva(preventivaId, it.competencia || modalAtendimento?.competencia || atendimento?.competencia);
                                                  }}
                                                >
                                                  {isRespondido ? "Alterar" : "Abrir preventiva"}
                                                </button>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {modalSessao && (
        <div
          onClick={fecharModalSessao}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "24px 12px",
            overflowY: "auto",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(980px, 100%)",
              maxHeight: "calc(100vh - 48px)",
              background: "#0b1220",
              border: "1px solid rgba(148,163,184,0.25)",
              borderRadius: 14,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "center",
                padding: 14,
                borderBottom: "1px solid rgba(148,163,184,0.15)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: "1rem" }}>Atendimento (sessao)</div>
                <div style={{ opacity: 0.8, fontSize: "0.85rem", marginTop: 4 }}>
                  {(() => {
                    const atendimento = sessoesAtendimentos[String(modalSessao.atendimentoId)] || {};
                    const clienteNome = clientesById[String(atendimento.clienteId)]?.nome || "-";
                    const comp = atendimento.competencia || "-";
                    const ini = modalSessao.inicioEm ? formatDataHora(modalSessao.inicioEm) : "-";
                    return `${clienteNome} • ${comp} • Inicio: ${ini}`;
                  })()}
                </div>
              </div>
              <button
                type="button"
                className={isMobile ? "m-btn-secondary" : "pc-btn pc-btn-secundario"}
                onClick={fecharModalSessao}
              >
                Fechar
              </button>
            </div>

            <div style={{ padding: 14, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
              {modalErro && (
                <div style={{ marginTop: 12, background: "#7f1d1d", color: "#fecaca", padding: 10, borderRadius: 10 }}>
                  {modalErro}
                </div>
              )}

              {modalLoading ? (
                <div style={{ padding: 14, opacity: 0.8 }}>Carregando...</div>
              ) : modalModo === "FINALIZAR" ? (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 12, fontSize: "1.1rem", color: "#fca5a5" }}>
                    ⚠️ Há serviços não finalizados neste atendimento
                  </div>
                  
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, opacity: 0.9 }}>Serviços pendentes:</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {pendentesFinalizacao.map((p) => (
                        <div
                          key={p.itemId}
                          style={{
                            border: "2px solid rgba(252, 165, 165, 0.4)",
                            borderRadius: 10,
                            padding: 12,
                            display: "flex",
                            gap: 10,
                            background: "rgba(127, 29, 29, 0.2)",
                          }}
                        >
                          <div style={{ fontSize: "1.2rem" }}>❌</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: "#fca5a5" }}>{p.tipo}</div>
                            <div style={{ opacity: 0.9, marginTop: 2, fontSize: "0.95rem" }}>{p.label || "-"}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Por que estes serviços não foram finalizados?</div>
                  <div style={{ marginBottom: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                    {MOTIVOS_NAO_REALIZADO.map((m) => (
                      <label key={m.id} style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={!!motivosFinalizacao[m.id]}
                          onChange={() => setMotivosFinalizacao((prev) => ({ ...prev, [m.id]: !prev[m.id] }))}
                        />
                        <span>{m.label}</span>
                      </label>
                    ))}
                  </div>

                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Observações (opcional)</div>
                  <textarea
                    value={obsFinalizacao}
                    onChange={(e) => setObsFinalizacao(e.target.value)}
                    rows={4}
                    placeholder="Descreva o contexto ou detalhes adicionais..."
                    style={{
                      width: "100%",
                      marginBottom: 16,
                      borderRadius: 10,
                      border: "1px solid rgba(148,163,184,0.25)",
                      padding: 10,
                      background: "rgba(15,23,42,0.6)",
                      color: "#e2e8f0",
                      fontFamily: "inherit",
                      fontSize: "0.95rem",
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      justifyContent: "flex-end",
                      marginTop: 16,
                      position: "sticky",
                      bottom: 0,
                      background: "#0b1220",
                      paddingTop: 12,
                      paddingBottom: 6,
                      borderTop: "1px solid rgba(148,163,184,0.15)",
                      zIndex: 1,
                    }}
                  >
                    <button
                      type="button"
                      className={isMobile ? "m-btn-secondary" : "pc-btn pc-btn-secundario"}
                      onClick={fecharModalSessao}
                      disabled={salvandoFinalizacao}
                    >
                      Voltar
                    </button>
                    <button
                      type="button"
                      className={isMobile ? "m-btn-warning" : "pc-btn pc-btn-primario"}
                      onClick={confirmarFinalizacaoComPendencias}
                      disabled={salvandoFinalizacao}
                    >
                      {salvandoFinalizacao ? "Finalizando..." : "Confirmar finalizacao"}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Itens desta sessao</div>
                  {modalItens.length === 0 ? (
                    <div style={{ opacity: 0.75 }}>Nenhum item nesta sessao.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {modalItens.map((it) => {
                        const tipo = String(it?.tipo || "").toUpperCase();
                        const isOs = tipo === "OS" || it?.osId;
                        const isPrev = tipo === "PREVENTIVA" || it?.preventivaId;
                        const isInstalacao =
                          tipo === "INSTALACAO" ||
                          (!!it?.equipamentoId && tipo === "EQUIPAMENTO" && !it?.osId && !it?.preventivaId) ||
                          (isOs && String(it?.osTipoServico || "").toLowerCase().includes("instal"));
                        
                        // Extrair preventivaId com fallback para o formato do id (PREVENTIVA:id:competencia)
                        let preventivaId = it?.preventivaId;
                        if (!preventivaId && tipo === "PREVENTIVA" && it?.id) {
                          const parts = String(it.id).split(":");
                          if (parts[0] === "PREVENTIVA" && parts[1]) {
                            preventivaId = parts[1];
                          }
                        }
                        
                        // Determinar se item está pendente
                        const isPendente = pendentesFinalizacao.some((p) => p.itemId === it.id);
                        const isRespondido = !isPendente;
                        
                        const label = isOs
                          ? (it?.osCodigo ? `#${it.osCodigo}` : "OS")
                          : (isInstalacao
                            ? (it?.equipamentoNome || "Instalação")
                            : (it?.preventivaCodigo || it?.preventivaNome || "Preventiva"));
                        const desc = isOs
                          ? (it?.osTitulo || it?.osId || "-")
                          : (isInstalacao
                            ? (it?.checklistInstalacaoNome || "Checklist de Instalação")
                            : (it?.preventivaNome || it?.maquinaNome || "-"));
                        const detalhes = [it?.setorNome, it?.maquinaNome].filter(Boolean).join(" • ");
                        const tipoLabel = isInstalacao ? "Instalação" : (isOs ? "OS" : "Preventiva");

                        return (
                          <div
                            key={it.id}
                            style={{
                              border: isPendente ? "2px solid rgba(252, 165, 165, 0.4)" : "1px solid rgba(148,163,184,0.2)",
                              borderRadius: 10,
                              padding: 12,
                              display: "flex",
                              gap: 10,
                              justifyContent: "space-between",
                              alignItems: "center",
                              background: isPendente ? "rgba(127, 29, 29, 0.15)" : "transparent",
                            }}
                          >
                            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: "1.3rem", marginTop: 2, flexShrink: 0 }}>
                                {isPendente ? "❌" : "✅"}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 700, color: isPendente ? "#fca5a5" : "#86efac" }}>
                                  {tipoLabel} • {label}
                                </div>
                                <div style={{ opacity: 0.85, marginTop: 2, fontSize: "0.9rem" }}>
                                  {[desc, detalhes].filter(Boolean).join(" • ")}
                                </div>
                                {isPendente && (
                                  <div style={{ marginTop: 6, fontSize: "0.85rem", color: "#fca5a5" }}>
                                    ⚠️ Pendente - clique no botão abaixo para finalizar
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end", flexShrink: 0 }}>
                              {(isOs || isInstalacao) && (
                                <button
                                  type="button"
                                  className={isMobile ? "m-btn-secondary" : "pc-btn pc-btn-secundario"}
                                  onClick={() => isInstalacao ? handleAbrirChecklistInstalacao(it.equipamentoId) : handleAbrirOs(it.osId)}
                                >
                                  {isRespondido ? "Alterar" : (isInstalacao ? "Executar Checklist" : "Abrir OS")}
                                </button>
                              )}
                              {isPrev && (
                                <button
                                  type="button"
                                  className={isMobile ? "m-btn-secondary" : "pc-btn pc-btn-secundario"}
                                  onClick={() => {
                                    console.log("DEBUG (modal): Item preventiva:", { it, preventivaId: preventivaId, tipo: tipo });
                                    handleAbrirPreventiva(preventivaId, it.competencia || modalAtendimento?.competencia || atendimento?.competencia);
                                  }}
                                >
                                  {isRespondido ? "Alterar" : "Abrir preventiva"}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
                    <button
                      type="button"
                      className={isMobile ? "m-btn-warning" : "pc-btn pc-btn-primario"}
                      onClick={() => handleFinalizarSessao(modalSessao)}
                    >
                      Finalizar atendimento
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );

  return (
    <Layout 
      tema={tema} 
      titulo="Meus Atendimentos"
      sidebarAberta={sidebarAberta}
      onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
    >
      {conteudo}
    </Layout>
  );
}

export default MeusAtendimentos;
